/**
 * 源谷 OA · Design System Tokens
 * Single source of truth for colors / type / radius / shadow / spacing.
 * Mirrors oa/project/design-system/tokens-icons.jsx (DS object).
 */

export const DS = {
  brand: {
    name: '源谷OA',
    full: '源谷OA系统',
    tagline: '团队协作 · 流程在线',
  },

  color: {
    // Primary scale — Mint / Emerald
    primary50: '#ecfdf5',
    primary100: '#d1fae5',
    primary200: '#a7f3d0',
    primary300: '#6ee7b7',
    primary400: '#34d399',
    primary500: '#10b981',
    primary600: '#059669',
    primary700: '#047857',
    primary800: '#065f46',

    // Neutrals — slate-warmed
    ink: '#0f172a',
    inkSoft: '#334155',
    text: '#334155',
    textSoft: '#475569',
    muted: '#64748b',
    disabled: '#cbd5e1',

    line: '#ececec',
    lineHard: '#e5e7eb',
    lineSoft: '#f1f3f1',

    bg: '#f7f7f5',
    panel: '#ffffff',
    hover: '#f1f3f1',
    selected: '#ecfdf5',

    success: '#10b981',
    successBg: '#ecfdf5',
    successFg: '#047857',

    warn: '#f59e0b',
    warnBg: '#fef3c7',
    warnFg: '#b45309',

    danger: '#ef4444',
    dangerBg: '#fee2e2',
    dangerFg: '#b91c1c',

    info: '#0ea5e9',
    infoBg: '#e0f2fe',
    infoFg: '#0369a1',

    cat: {
      blue: { fg: '#0369a1', bg: '#e0f2fe', dot: '#0ea5e9' },
      violet: { fg: '#6d28d9', bg: '#ede9fe', dot: '#8b5cf6' },
      pink: { fg: '#be185d', bg: '#fce7f3', dot: '#ec4899' },
      amber: { fg: '#b45309', bg: '#fef3c7', dot: '#f59e0b' },
      mint: { fg: '#047857', bg: '#ecfdf5', dot: '#10b981' },
      red: { fg: '#b91c1c', bg: '#fee2e2', dot: '#ef4444' },
      slate: { fg: '#334155', bg: '#f1f5f9', dot: '#64748b' },
    },
  },

  font: {
    sans: "'Inter','Noto Sans SC','AlibabaSans',system-ui,-apple-system,sans-serif",
    mono: "'JetBrains Mono','SF Mono',ui-monospace,monospace",
  },

  type: {
    display: {
      fontSize: 28,
      fontWeight: 600,
      letterSpacing: '-0.02em',
      lineHeight: 1.15,
    },
    h1: {
      fontSize: 22,
      fontWeight: 600,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
    },
    h2: {
      fontSize: 18,
      fontWeight: 600,
      letterSpacing: '-0.015em',
      lineHeight: 1.3,
    },
    h3: {
      fontSize: 15,
      fontWeight: 600,
      letterSpacing: '-0.005em',
      lineHeight: 1.35,
    },
    body: { fontSize: 13.5, fontWeight: 400, lineHeight: 1.5 },
    bodySm: { fontSize: 12.5, fontWeight: 400, lineHeight: 1.5 },
    label: { fontSize: 12.5, fontWeight: 500, lineHeight: 1.4 },
    caption: { fontSize: 11.5, fontWeight: 500, lineHeight: 1.4 },
    overline: {
      fontSize: 11,
      fontWeight: 600,
      letterSpacing: '0.06em',
      textTransform: 'uppercase' as const,
      lineHeight: 1.4,
    },
    num: {
      fontVariantNumeric: 'tabular-nums' as const,
      letterSpacing: '-0.01em',
    },
  },

  radius: {
    xs: 4,
    sm: 6,
    md: 8,
    lg: 10,
    xl: 12,
    xxl: 16,
    pill: 999,
  },

  shadow: {
    none: 'none',
    xs: '0 1px 2px rgba(15, 23, 42, .04)',
    sm: '0 1px 2px rgba(15, 23, 42, .05), 0 0 0 1px rgba(15, 23, 42, .04)',
    md: '0 2px 6px rgba(15, 23, 42, .06), 0 0 0 1px rgba(15, 23, 42, .04)',
    lg: '0 8px 24px rgba(15, 23, 42, .08), 0 0 0 1px rgba(15, 23, 42, .04)',
    primary:
      '0 6px 16px -6px rgba(16, 185, 129, .55), inset 0 1px 0 rgba(255, 255, 255, .25)',
    focus: '0 0 0 3px rgba(16, 185, 129, .18)',
  },

  space: { 0: 0, 1: 4, 2: 8, 3: 12, 4: 16, 5: 20, 6: 24, 7: 28, 8: 32, 10: 40 },

  layout: {
    topbarH: 56,
    sidebarW: 220,
    sidebarCollapsedW: 60,
    pageGutter: 24,
    pagePadY: 20,
    rowH: 44,
  },

  motion: {
    fast: 'all .12s ease',
    base: 'all .18s cubic-bezier(.2,.7,.3,1)',
    bounce: 'all .24s cubic-bezier(.34,1.56,.64,1)',
  },
} as const;

export type CategoryTone = keyof typeof DS.color.cat;

/** antd v5 ConfigProvider theme tokens derived from DS */
export const antdTheme = {
  token: {
    colorPrimary: DS.color.primary500,
    colorSuccess: DS.color.success,
    colorWarning: DS.color.warn,
    colorError: DS.color.danger,
    colorInfo: DS.color.info,
    colorLink: DS.color.primary600,
    colorLinkHover: DS.color.primary500,
    colorLinkActive: DS.color.primary700,
    colorTextBase: DS.color.ink,
    colorBgBase: DS.color.panel,
    colorBgLayout: DS.color.bg,
    colorBorder: DS.color.lineHard,
    colorBorderSecondary: DS.color.line,
    borderRadius: DS.radius.md,
    borderRadiusLG: DS.radius.xl,
    borderRadiusSM: DS.radius.sm,
    fontFamily: DS.font.sans,
    fontSize: 13,
    controlHeight: 34,
    controlHeightSM: 28,
    controlHeightLG: 40,
    boxShadow: DS.shadow.sm,
    boxShadowSecondary: DS.shadow.md,
  },
  components: {
    Button: {
      controlHeight: 34,
      borderRadius: DS.radius.md,
      fontWeight: 500,
      primaryShadow: 'none',
    },
    Card: {
      borderRadiusLG: DS.radius.xl,
      headerBg: 'transparent',
      headerFontSize: 15,
      paddingLG: 20,
    },
    Table: {
      headerBg: DS.color.lineSoft,
      headerColor: DS.color.inkSoft,
      headerSplitColor: 'transparent',
      borderColor: DS.color.lineSoft,
      rowHoverBg: DS.color.lineSoft,
      headerSortActiveBg: DS.color.lineSoft,
      cellPaddingBlock: 12,
      fontSize: 13,
    },
    Tabs: {
      itemSelectedColor: DS.color.ink,
      itemHoverColor: DS.color.ink,
      itemActiveColor: DS.color.primary700,
      inkBarColor: DS.color.primary500,
      titleFontSize: 13,
    },
    Menu: {
      itemActiveBg: DS.color.primary50,
      itemSelectedBg: DS.color.primary50,
      itemSelectedColor: DS.color.primary700,
      itemHoverBg: DS.color.lineSoft,
      itemHoverColor: DS.color.ink,
      itemBorderRadius: DS.radius.md,
      itemHeight: 38,
      itemMarginInline: 6,
    },
    Input: {
      controlHeight: 34,
      borderRadius: DS.radius.md,
      activeBorderColor: DS.color.primary500,
      hoverBorderColor: DS.color.primary400,
    },
    Select: {
      controlHeight: 34,
      borderRadius: DS.radius.md,
    },
    Modal: {
      borderRadiusLG: DS.radius.xl,
      titleFontSize: 18,
    },
    Tag: {
      borderRadiusSM: DS.radius.sm,
    },
    Badge: {
      fontWeight: 600,
    },
    Pagination: {
      itemActiveBg: DS.color.primary50,
    },
    Progress: {
      defaultColor: DS.color.primary500,
    },
    Switch: {
      colorPrimary: DS.color.primary500,
    },
    Layout: {
      bodyBg: DS.color.bg,
      headerBg: DS.color.panel,
      siderBg: DS.color.panel,
      headerHeight: DS.layout.topbarH,
    },
  },
};
